<script setup lang="ts">
  import { computed } from "vue";
  import { RouterLink, useRoute } from "vue-router";
  import { Tooltip } from "@/components/ui/tooltip";
  import SurveyStatusBadge from "@/components/Survey/SurveyStatusBadge.vue";
  import { useBreadcrumbs } from "@/utils/composables/useBreadcrumbs";
  import AppHeaderActions from "./AppHeaderActions.vue";
  import AppHeaderTabs from "./AppHeaderTabs.vue";
  import { useAppHeaderTabs } from "./useAppHeaderTabs";
  import AssistantLauncher from "@/components/App/Assistant/AssistantLauncher.vue";
  import { useAssistantScope } from "@/composables/useAssistantScope";
  import { useAssistantStore } from "@/stores/assistant";

  defineProps({
    user: { type: Object, default: () => ({}) },
    isLoggedIn: { type: Boolean, default: false },
    survey: { type: Object, default: () => ({}) },
    isAdmin: { type: Boolean, default: false },
    isSuperAdmin: { type: Boolean, default: false },
    isSale: { type: Boolean, default: false },
  });

  const { heading } = useBreadcrumbs();
  const { hasTabs } = useAppHeaderTabs();

  const assistant = useAssistantStore();
  const { scope } = useAssistantScope();
  const route = useRoute();
  const showLauncher = computed(() => Boolean(scope.value) && route.name !== "EpistoAi");
</script>

<template>
  <div
    :class="[
      'grid items-center px-4',
      hasTabs ? 'grid-cols-[1fr_auto_1fr] gap-4' : 'grid-cols-[1fr_auto]',
      heading.title || hasTabs ? 'h-[60px]' : 'min-h-0',
    ]">
    <div
      v-if="heading.title"
      class="flex min-w-0 items-center gap-2.5"
      data-cy="page-heading">
      <Tooltip :content="heading.title.label">
        <component
          :is="heading.title.to ? RouterLink : 'span'"
          :to="heading.title.to"
          class="heading-title block min-w-0 truncate"
          :class="heading.title.to && 'transition-colors hover:text-foreground-secondary'">
          {{ heading.title.label }}
        </component>
      </Tooltip>
      <SurveyStatusBadge
        v-if="heading.status && heading.status !== 'complete'"
        :status="heading.status"
        class="shrink-0" />
      <template
        v-for="(item, index) in heading.trail"
        :key="index">
        <span class="shrink-0 text-foreground-subtle">·</span>
        <component
          :is="item.to ? RouterLink : 'span'"
          :to="item.to"
          class="min-w-0 truncate text-13"
          :class="item.to ? 'text-muted-foreground transition-colors hover:text-primary' : 'text-foreground'">
          {{ item.label }}
        </component>
      </template>
    </div>
    <div v-else />

    <div
      v-if="hasTabs"
      class="flex h-full justify-center self-stretch">
      <AppHeaderTabs />
    </div>

    <div class="flex items-center justify-end gap-4">
      <AppHeaderActions />
      <AssistantLauncher
        v-if="showLauncher"
        :state="assistant.launcherState"
        :open="assistant.open"
        @click="assistant.toggle()" />
    </div>
  </div>
</template>
